import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { updateWeeklyMaster, WeeklySalesSummary } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  "Upload your rolling Master.xlsx and this week's Input.xlsx (the Profit & Loss export, week label in column B row 7).",
  'Optionally upload Account Transactions.xlsx to fill the Avg Temp / Perm Fee rows.',
  'Click Update Master & Summary. The updated Master.xlsx downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function WeeklySalesPanel({ mission, onBack }: PanelProps) {
  const [master, setMaster] = useState<File[]>([]);
  const [input, setInput] = useState<File[]>([]);
  const [account, setAccount] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<WeeklySalesSummary>>();

  const handleSubmit = async () => {
    if (master.length === 0 || input.length === 0) {
      fail('Upload both Master.xlsx and Input.xlsx.');
      return;
    }
    await run(() => updateWeeklyMaster(master[0], input[0], account[0] ?? null), 'Updating the master workbook failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 What happens on each run</p>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>The new week column is inserted (or updated in place if it already exists).</li>
          <li>Derived rows (Total Turnover, GP, GP Margin etc.) are recalculated.</li>
          <li>The Summary sheet is refreshed with monthly aggregates and weekly rows.</li>
          <li>The updated Master.xlsx is returned. Keep your own backup of the original.</li>
        </ol>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop
          label="Master.xlsx"
          note="Your rolling history workbook."
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={master}
          onFiles={setMaster}
        />
        <FileDrop
          label="Input.xlsx"
          note="This week's P&L export."
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={input}
          onFiles={setInput}
        />
      </div>
      <FileDrop
        label="Account Transactions.xlsx (optional)"
        note="Sheet 1 = Temp invoice refs; the 'Permanant Invoice Transactions' sheet = Perm. Used for the Avg Fee rows."
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={account}
        onFiles={setAccount}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Updating…' : 'Update Master & Summary'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Updating…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ {result.filename} updated and downloaded{s ? ` (week: ${s.week_label})` : ''}.
          </div>
          {s?.messages
            .filter((m) => m.text)
            .map((m, i) => (
              <div
                key={i}
                className={`rounded-lg px-4 py-3 ${
                  m.level === 'warning'
                    ? 'bg-amber-50 text-amber-800'
                    : m.level === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-blue-50 text-blue-700'
                }`}
              >
                {m.text}
              </div>
            ))}
          {s && s.log.length > 0 && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">📋 Processing log</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {s.log.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload Master.xlsx and Input.xlsx to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default WeeklySalesPanel;
